import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AdminProperties from "./admin/AdminProperties";
import AdminApprovals from "./admin/AdminApprovals";
import AdminReels from "./admin/AdminReels";
import AdminBonuses from "./admin/AdminBonuses";
import AdminSettings from "./admin/AdminSettings";
import AdminLeaders from "./admin/AdminLeaders";
import AdminImport from "./admin/AdminImport";
import AdminUsers from "./admin/AdminUsers";
import AdminTasks from "./admin/AdminTasks";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [pulse, setPulse] = useState(null);
  const loadStats = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/pulse").then(({ data }) => setPulse(data));
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#c8102e] mb-2">Support Team</p>
      <h1 className="font-display text-3xl font-bold text-[#1a1f36] mb-6">Admin control center</h1>

      {pulse && (
        <div className="sw-navy text-white rounded-md px-5 py-4 mb-4" data-testid="pulse-card">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="shrink-0">
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#ff4d64]">Pulse</div>
              <div className="font-display font-bold text-xl" data-testid="pulse-day-label">{pulse.day_label}</div>
              <div className="text-[11px] text-slate-400">{new Date(pulse.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {[["Signups today", pulse.signups_today, "signups-today"],
                ["Signed in today", pulse.signed_in_today, "signed-in-today"],
                ["Submissions today", pulse.submissions_today, "subs-today"],
                ["Approvals today", pulse.approvals_today, "approvals-today"],
                ["Teams active today", pulse.teams_active_today, "teams-active-today"],
                ["Floors claimed", `${pulse.floors_claimed}/${pulse.total_floors}`, "floors-claimed"],
                ["Properties claimed", `${pulse.properties_claimed}/${pulse.claimable_properties}`, "props-claimed"]].map(([label, val, id]) => (
                <div key={label}>
                  <div className="font-display font-bold text-2xl" data-testid={`pulse-${id}`}>{val}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8 stagger">
          {[["Properties", stats.total_properties, "total-properties"], ["Enrolled", stats.enrolled, "enrolled"], ["On the board", stats.on_board, "on-board"], ["Needs capacity", stats.needs_capacity, "needs-capacity"], ["Pending subs", stats.pending_submissions, "pending-subs"], ["Approved subs", stats.approved_submissions, "approved-subs"], ["Reels", stats.reels, "reels"]].map(([label, val, id]) => (
            <div key={label} className="border border-slate-200 rounded-md p-3">
              <div className="text-2xl font-display font-bold text-[#1a1f36]" data-testid={`admin-stat-${id}`}>{val}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="properties" onValueChange={loadStats}>
        <TabsList className="w-full flex overflow-x-auto justify-start mb-4">
          <TabsTrigger value="properties" data-testid="admin-tab-properties">Properties</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="admin-tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="admin-tab-approvals">Approval queue</TabsTrigger>
          <TabsTrigger value="reels" data-testid="admin-tab-reels">Reel judging</TabsTrigger>
          <TabsTrigger value="bonuses" data-testid="admin-tab-bonuses">Bonuses</TabsTrigger>
          <TabsTrigger value="leaders" data-testid="admin-tab-leaders">Mid-Campaign</TabsTrigger>
          <TabsTrigger value="import" data-testid="admin-tab-import">Import users</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="properties"><AdminProperties /></TabsContent>
        <TabsContent value="users"><AdminUsers /></TabsContent>
        <TabsContent value="tasks"><AdminTasks /></TabsContent>
        <TabsContent value="approvals"><AdminApprovals onChanged={loadStats} /></TabsContent>
        <TabsContent value="reels"><AdminReels /></TabsContent>
        <TabsContent value="bonuses"><AdminBonuses /></TabsContent>
        <TabsContent value="leaders"><AdminLeaders /></TabsContent>
        <TabsContent value="import"><AdminImport /></TabsContent>
        <TabsContent value="settings"><AdminSettings /></TabsContent>
      </Tabs>
    </div>
  );
}
